import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Timestamp,
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { useEffect, useMemo } from "react";

import {
  StoryJob,
  StoryJobInput,
  StoryJobStatus,
  StoryTrack,
  isTranslationJob,
} from "@baiki/interfaces";

import { db } from "@/lib/firebase";
import { useAuth } from "@/providers";

/**
 * Convert Firestore Timestamp to Date
 */
function timestampToDate(timestamp: unknown): Date {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (
    timestamp &&
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof timestamp.toDate === "function"
  ) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date();
}

/**
 * Convert Firestore document data to StoryJob
 */
function convertFirestoreDataToStoryJob(
  id: string,
  data: Record<string, unknown>,
): StoryJob {
  const progress = data.progress as {
    status: StoryJobStatus;
    percentage: number;
    message: string;
    updatedAt: unknown;
  };
  const result = data.result as
    | {
        storyId: string;
        story: { title: string; summary: string; text: string };
        tracks: unknown[];
        usage?: unknown;
      }
    | undefined;
  const error = data.error as
    | { code: string; message: string; timestamp: unknown; retryable: boolean }
    | undefined;

  return {
    id,
    input: data.input as StoryJob["input"],
    progress: {
      status: progress.status,
      percentage: progress.percentage,
      message: progress.message,
      updatedAt: timestampToDate(progress?.updatedAt),
    },
    result: result
      ? {
          storyId: result.storyId,
          story: result.story,
          tracks: result.tracks as StoryTrack[],
          usage: (result.usage as {
            textGenerationTokens: { input: number; output: number };
          }) || {
            textGenerationTokens: { input: 0, output: 0 },
          },
        }
      : undefined,
    error: error
      ? {
          code: error.code,
          message: error.message,
          timestamp: timestampToDate(error.timestamp),
          retryable: error.retryable,
        }
      : undefined,
    retryCount: (data.retryCount as number) || 0,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
    completedAt: data.completedAt
      ? timestampToDate(data.completedAt)
      : undefined,
  };
}

/**
 * Create a new job and return the job ID.
 * Client should then listen to job updates via useJob hook.
 */
export const useCreateJob = () => {
  return useMutation({
    mutationFn: async (input: StoryJobInput): Promise<string> => {
      if (!db) {
        throw new Error("Firestore is not initialized");
      }

      console.log(`[useCreateJob] Creating job with input:`, {
        kind: input.kind ?? "story",
        language: input.language,
        authorId: input.authorId,
        ...(isTranslationJob(input)
          ? {
              sourceStoryId: input.sourceStoryId,
              sourceTitle: input.sourceTitle,
            }
          : {
              who: input.who,
              what: input.what,
              hasVoiceConfig: !!input.voiceConfig,
            }),
      });

      const jobsRef = collection(db, "jobs");
      const jobDoc = await addDoc(jobsRef, {
        input,
        progress: {
          status: StoryJobStatus.PENDING,
          percentage: 0,
          message: "Starting story generation...",
          updatedAt: serverTimestamp(),
        },
        retryCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      console.log(
        `[useCreateJob] Job created successfully with ID: ${jobDoc.id}, language: ${input.language}`,
      );

      return jobDoc.id;
    },
  });
};

/**
 * Real-time listener for job updates.
 * Automatically updates when job status changes.
 */
export const useJob = (jobId?: string) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["job", jobId], [jobId]);

  // Set up real-time listener using useEffect
  useEffect(() => {
    if (!db || !jobId) {
      queryClient.setQueryData(queryKey, null);
      return;
    }

    const jobRef = doc(db, "jobs", jobId);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      jobRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const job = convertFirestoreDataToStoryJob(snapshot.id, data);
          // Update React Query cache with new data
          queryClient.setQueryData(queryKey, job);
        } else {
          queryClient.setQueryData(queryKey, null);
        }
      },
      (error) => {
        console.error("Error listening to job:", error);
      },
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [jobId, queryClient, queryKey]);

  // Return query with initial data
  return useQuery<StoryJob | null>({
    queryKey,
    queryFn: () => null, // Dummy function, data managed by useEffect
    enabled: false, // Don't fetch, we're using the listener
    initialData: null,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Real-time listener for a customer's active jobs.
 * Returns jobs that are in progress (not completed or failed).
 */
export const useCustomerActiveJobs = (authorId?: string) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["customerActiveJobs", authorId], [authorId]);

  // Set up real-time listener using useEffect
  useEffect(() => {
    if (!db || !authorId) {
      queryClient.setQueryData(queryKey, []);
      return;
    }

    const jobsRef = collection(db, "jobs");
    const activeJobsQuery = query(
      jobsRef,
      where("input.authorId", "==", authorId),
      where("progress.status", "in", [
        StoryJobStatus.PENDING,
        StoryJobStatus.GENERATING_TEXT,
        StoryJobStatus.GENERATING_AUDIO,
        StoryJobStatus.UPLOADING,
      ]),
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      activeJobsQuery,
      (snapshot) => {
        const jobs: StoryJob[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          jobs.push(convertFirestoreDataToStoryJob(doc.id, data));
        });
        // Update React Query cache with new data
        queryClient.setQueryData(queryKey, jobs);
      },
      (error) => {
        console.error("Error listening to active jobs:", error);
      },
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [authorId, queryClient, queryKey]);

  // Return query with initial empty data
  return useQuery<StoryJob[]>({
    queryKey,
    queryFn: () => [], // Dummy function, data managed by useEffect
    enabled: false, // Don't fetch, we're using the listener
    initialData: [],
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * One job at a time: while anything is in flight — a story being written OR a
 * translation being made — no new work may be started. Both kinds block both
 * actions; they share the pipeline and the same 5-sparkle charge.
 *
 * The single place that answers "is the user busy?", so no surface re-derives
 * it from a job list inline.
 */
const useHasActiveJob = (): boolean => {
  const { user } = useAuth();
  const { data: jobs = [] } = useCustomerActiveJobs(user?.uid);
  return jobs.length > 0;
};

export const JobsQueries = {
  useCreateJob,
  useJob,
  useCustomerActiveJobs,
  useHasActiveJob,
};
